<template>
    <div>
        <v-row>
            <v-col class="py-1" cols="12">
                <v-select
                    v-model="selectedFunction"
                    :items="functionItems"
                    item-title="label"
                    item-value="key"
                    label="Output behaviour"
                    density="comfortable"
                    @update:model-value="onFunctionChange"
                />
            </v-col>

            <v-col v-if="showBeacon" class="py-1" cols="12">
                <v-select
                    v-model="selectedBeacon"
                    :items="beaconItems"
                    item-title="label"
                    item-value="key"
                    label="Pulse pattern"
                    density="comfortable"
                    @update:model-value="emitFields"
                />
            </v-col>

            <v-col v-if="showEdge" class="py-1" cols="12">
                <v-select
                    v-model="selectedEdge"
                    :items="edgeItems"
                    item-title="label"
                    item-value="key"
                    label="Pulse width"
                    density="comfortable"
                    @update:model-value="emitFields"
                />
            </v-col>

            <v-col v-if="showAdvanced" class="py-1" cols="12">
                <v-alert
                    density="compact"
                    type="info"
                    variant="tonal"
                    text="Set the raw motion fields below. Use this only if none of the presets fit."
                />
            </v-col>

            <v-col class="py-1" cols="12">
                <div class="text-caption text-medium-emphasis">{{ preview }}</div>
            </v-col>
        </v-row>
    </div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue';
import {
    OUTPUT_FUNCTIONS,
    BEACON_PATTERNS,
    EDGE_PULSE_WIDTHS,
    fieldsToPreset,
    previewText,
} from './IoFunctionPresets';

const props = defineProps({
    // existing config values, used to detect which preset is selected on mount
    modelValue: {
        type: Object,
        default: () => ({}),
    },
});

const emit = defineEmits(['update:fields', 'legacyMigration']);

const functionItems = OUTPUT_FUNCTIONS.map(f => ({ key: f.key, label: f.label }));
const beaconItems = BEACON_PATTERNS;
const edgeItems = EDGE_PULSE_WIDTHS;

const selectedFunction = ref('off');
const selectedBeacon = ref(BEACON_PATTERNS[0].key);
const selectedEdge = ref(EDGE_PULSE_WIDTHS[1].key); // Medium default

// Detect initial preset from incoming fields once on mount. Avoid a deep watcher
// here so the picker does not re-pick its own selection while the customer is still
// editing the form (the parent updates fields via Object.assign on every change).
function syncFromModel() {
    const detected = fieldsToPreset(props.modelValue || {});
    selectedFunction.value = detected.functionKey;
    if (detected.beaconKey) selectedBeacon.value = detected.beaconKey;
    if (detected.edgeKey) selectedEdge.value = detected.edgeKey;
    // Normalise saved templates that were stored with sloppy field combinations.
    // If the detected preset is a known one (not advanced), re-emit the canonical
    // field set so the parent form ends up with the preset's exact shape. Skip for
    // advanced so the user's raw field values are preserved. For advanced templates
    // we still emit the advanced flag so the parent shows the raw-fields panel on
    // first paint instead of waiting for the next user interaction.
    if (detected.functionKey === 'advanced') {
        emit('update:fields', { advanced: true });
    } else if (detected.functionKey) {
        if (detected.isLegacyMigration) {
            emit('legacyMigration');
        }
        emitFields();
    }
}
onMounted(syncFromModel);

const showBeacon = computed(() => selectedFunction.value === 'beacon_while_moving');
const showEdge = computed(() =>
    ['pulse_on_start', 'pulse_on_end', 'pulse_on_both'].includes(selectedFunction.value)
);
const showAdvanced = computed(() => selectedFunction.value === 'advanced');

const preview = computed(() =>
    previewText(selectedFunction.value, {
        beacon: selectedBeacon.value,
        edge: selectedEdge.value,
    })
);

function onFunctionChange() {
    emitFields();
}

function emitFields() {
    const preset = OUTPUT_FUNCTIONS.find(f => f.key === selectedFunction.value);
    if (!preset || preset.key === 'advanced') {
        emit('update:fields', { advanced: true });
        return;
    }
    const fields = preset.fields({
        beacon: selectedBeacon.value,
        edge: selectedEdge.value,
    });
    emit('update:fields', { advanced: false, fields });
}
</script>
